import React from 'react';
import { Box } from '@mui/material';
import CartLoader from '../../../../../Helper/Loaders/CartLoader';
import { useTranslation } from 'react-i18next';
import { calculateDynamicRange, getTooltipHtmlForComparison } from '../../../../../Helper/DigitalTwin/DigitalTwinFunctions';
import BaseVitalChart from '../../../components/BaseVitalChart';
import {
    getBaseCanvasSettings,
    getBaseChartSettings,
    getBaseTooltipSettings,
    getBaseXAxisSettings,
    isBaseDataSeriesEmpty
} from '../../../../../Helper/DigitalTwin/VitalChartConfigurations';

const VitalsComparisonChart = ({
    series,
    categories,
    startDate,
    isLoading,
    primaryGranularity,
    currentMetric,
    resetToLatestDataDate = () => { },
    chartID = "chartID",
    chartKey,
    zoomResetViewRef,
    onZoom = () => {},
    onZoomReset = () => {},
    zoomedRange = null,
    setLoading = null,
    isZoomed = false

}) => {

    const { t } = useTranslation()
    const chartHeight = 237

    const getChartSettings = () => {
        const baseChartSettings = getBaseChartSettings(chartID, onZoom, setLoading)
        return { ...baseChartSettings, height: chartHeight, group: 'comparison' }
    }

    const getYAxisSettings = () => {
        let ranges = currentMetric.ranges
        if (series && series.length > 0) {
            // Combine all y values from all series into a single array
            // This is efficient even with 20-30 series as we only extract y values
            const combinedData = series.reduce((acc, seriesItem) => {
                if (seriesItem && seriesItem.data && Array.isArray(seriesItem.data)) {
                    seriesItem.data.forEach(dataPoint => {
                        if (dataPoint && dataPoint.y !== null && dataPoint.y !== undefined) {
                            if (Array.isArray(dataPoint.y)) {
                                acc.push({ y: dataPoint.y[0] }, { y: dataPoint.y[1] });
                            } else {
                                acc.push({ y: dataPoint.y });
                            }
                        }
                    });
                }
                return acc;
            }, []);

            if (combinedData.length > 0) {
                ranges = calculateDynamicRange(combinedData, ranges.min, ranges.max)
            }
        }
        return {
            tickAmount: 5,
            position: 'top',
            min: ranges.min,
            max: ranges.max,
            axisTicks: {
                show: true,
                borderType: 'dotted'
            },

            title: {
                text: currentMetric.name,
                position: 'high',
                rotate: -90,
                style: {
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#494949',
                },
            },
            labels: {
                show: true,
                offsetX: 0,
                offsetY: 0,
                style: {
                    colors: '#87939D',
                    fontSize: '10px',
                    fontWeight: 500,
                    fontfamily: 'Roboto !important'
                },
                formatter: (value) => Math.round(value),
            },
        }
    }

    const getXAxisSettings = () => {
        const settings = getBaseXAxisSettings(primaryGranularity, categories, startDate, zoomedRange)
        return settings
    }

    const getToolTipSettings = () => {
        const tooltipSettings = getBaseTooltipSettings()
        tooltipSettings.enabled = series.length > 0
        tooltipSettings.followCursor = false
        tooltipSettings.custom = (opts) => getTooltipHtmlForComparison(opts, t)
        return tooltipSettings
    }

    const isDataSeriesEmpty = () => {
        return isBaseDataSeriesEmpty(series)
    }

    const getCanvasSettings = () => {
        const base = getBaseCanvasSettings(primaryGranularity, series)
        const hasRangeArea = series.some(s => s.type === 'rangeArea')
        if (!hasRangeArea) return base
        const fillOpacities = series.map(s => s.type === 'rangeArea' ? 0.15 : 1)
        const strokeWidths = series.map(s => s.type === 'rangeArea' ? 0 : 2)
        base.fill = { ...base.fill, opacity: fillOpacities }
        base.stroke = { ...base.stroke, width: strokeWidths }
        return base
    }

    const getChartOptions = () => {
        return {
            ...getCanvasSettings(),
            chart: getChartSettings(),
            xaxis: getXAxisSettings(),
            yaxis: getYAxisSettings(),
            tooltip: getToolTipSettings(),
        };
    }

    if (isLoading) {
        return (
            <Box sx={{
                height: `${chartHeight}px`,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <CartLoader />
            </Box>
        );
    }

    return (
        <BaseVitalChart
            options={getChartOptions()}
            series={series}
            chartID={chartID}
            chartHeight={chartHeight}
            disableZoomReset={true}
            primaryGranularity={primaryGranularity}
            resetToLatestDataDate={resetToLatestDataDate}
            isDataSeriesEmpty={isDataSeriesEmpty}
            zoomResetViewRef={zoomResetViewRef}
            chartKey={chartKey}
            chartProps={{
                width: '100%',
            }}
            onZoomReset={onZoomReset}
            isZoomed={isZoomed}
        />
    );
};

export default VitalsComparisonChart;
